import React, { type ComponentType, ReactElement } from 'react';
import { VStack, HStack, Text, SimpleGrid, Box, Button } from '@chakra-ui/react';
import { NovaIcon, type NovaIconGlyphProps } from 'components/ui/novaIcon';
import { MedicationSearchResult, ParsedArticleSearchResult } from '@novaheal/types';
import { VideoSearchResult } from '@novaheal/types/lib/search/videoSearch';
import { ImageSearchResult } from '@novaheal/types/lib/search/imageSearch';
import {
  isArticleSearchResult,
  isImageSearchResult,
  isMedicationSearchResult,
  isVideoSearchResult,
} from 'types/SearchResultWithParsedArticle';
import placeholderImg from 'assets/common/placeholder.png';
import { MedicationCard } from 'components/common/MedicationCard';
import { ChevronRightIcon } from 'assets/icons/v2';
import { SearchResult } from './SearchResult';
import { SearchResultText } from './SearchResultText';
import { VideoCard } from './VideoCard';
import { ImageCard } from './ImageCard';

type SearchResultWithParsedArticle =
  | MedicationSearchResult
  | ParsedArticleSearchResult
  | VideoSearchResult
  | ImageSearchResult;

type Props = {
  searchResults: SearchResultWithParsedArticle[];
  count: number;
  query?: string;
  numOfColumns?: number;
  Icon: ComponentType<NovaIconGlyphProps>;
  title: string;
  footerElement?: ReactElement | null;
  onSeeMoreClick?: () => void;
};

const renderBasedOnType = (result: SearchResultWithParsedArticle): ReactElement | null => {
  if (isArticleSearchResult(result)) {
    return (
      <SearchResult
        key={result.objectID}
        id={result.objectID}
        title={result.fields.title}
        imageUrl={result.fields.coverImage?.url || placeholderImg}
        subtitle={result.fields.category.name}
        hasVideo={result.fields.hasVideo}
        relevantYearNames={result.fields.relevantYears}
        description={result.highlightData?.value}
        csId={result.highlightData?.csId}
      />
    );
  }
  if (isMedicationSearchResult(result)) {
    return (
      <MedicationCard
        key={result.objectID}
        id={result.objectID}
        name={result.fields.name}
        trademark={result.fields.trademark}
        indications={result.fields.indications}
      />
    );
  }
  if (isVideoSearchResult(result)) {
    return (
      <VideoCard
        key={result.objectID}
        title={result.fields.title}
        thumbnailUrl={result.fields.thumbnailUrl}
        description={result.fields.description}
        videoUrl={result.fields.videoUrl}
      />
    );
  }

  if (isImageSearchResult(result)) {
    return (
      <ImageCard
        key={result.objectID}
        title={result.fields.title}
        imageUrl={result.fields.imageUrl}
        description={result.fields.description}
      />
    );
  }

  return null;
};

export const SearchResultListAllFragment = ({
  searchResults,
  count,
  query,
  numOfColumns = 1,
  Icon,
  title,
  footerElement,
  onSeeMoreClick,
}: Props): ReactElement => {
  const isHorizontal =
    searchResults.length > 0 &&
    (isImageSearchResult(searchResults[0]) || isVideoSearchResult(searchResults[0]));

  return (
    <VStack
      bg="background.primary"
      p={{ base: '15px 20px', md: '20px' }}
      align="left"
      gap="15px"
      width="100%"
      borderRadius="10px"
    >
      <HStack gap="5px" alignItems="center" width="100%">
        {Icon && <NovaIcon icon={Icon} size="s" color="text.secondary" aria-hidden />}
        <Text textStyle="h4.regular" color="text.secondary">
          {title}
        </Text>
        <Box flex="1" />
        <Button
          bg="transparent"
          color="text.tertiary"
          outline="none"
          p="0"
          m="0"
          height="fit-content"
          display="flex"
          gap="5px"
          alignItems="center"
          variant="tertiary"
          _focus={{
            outline: 'none',
            background: 'transparent',
          }}
          _hover={{
            background: 'transparent',
          }}
          onClick={onSeeMoreClick}
        >
          <Text textStyle="body.m.regular">Alle ansehen </Text>
          {/* Custom icon due to missing size */}
          <ChevronRightIcon boxSize="12px" />
        </Button>
      </HStack>

      <SearchResultText count={count} searchQuery={query} />

      {isHorizontal ? (
        <HStack
          gap="s"
          overflowX="auto"
          overflowY="hidden"
          width="100%"
          css={{
            '&::-webkit-scrollbar': {
              display: 'none',
            },
            '-ms-overflow-style': 'none',
            'scrollbar-width': 'none',
          }}
        >
          {searchResults.map((result) => (
            <Box key={result.objectID} minW="160px">
              {renderBasedOnType(result)}
            </Box>
          ))}
        </HStack>
      ) : (
        <SimpleGrid
          columns={numOfColumns}
          rowGap={
            numOfColumns > 1
              ? {
                  base: 4,
                  md: '36px',
                }
              : '20px'
          }
          columnGap={
            numOfColumns > 1
              ? {
                  base: 4,
                  md: '20px',
                }
              : 0
          }
          width="100%"
        >
          {searchResults.map((result) => (
            <React.Fragment key={result.objectID}>{renderBasedOnType(result)}</React.Fragment>
          ))}
        </SimpleGrid>
      )}

      {footerElement && footerElement}
    </VStack>
  );
};
